import { Component, effect, inject, signal, viewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import {
  NotificationService,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCenterComponent,
  OuiRecordDatasource as OuiDatasource,
  OuiFileDropZoneComponent,
  OuiRowComponent,
  OuiStackComponent,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';

import { CardComponent } from '../../../components/card/card.component';
import { DownloadButtonComponent } from '../../../components/download-button/download-button.component';
import { LogTableComponent } from '../../../components/log-table/log-table.component';
import { PageComponent } from '../../../components/page/page';
import { SelectComponent } from '../../../components/select/select.component';
import { ValidationErrorsDialogComponent } from '../../../components/validation-errors-dialog/validation-errors-dialog.component';
import { WorkflowLogEntry } from '../../../models/workflow-history.model';
import { BusinessEntityService } from '../../../services/business-entity/business-entity.service';
import { BuyerManagementService } from '../../../services/buyer-management/buyer-management.service';
import { BasePageComponent } from '../../base/base.component';

@Component({
  selector: 'app-buyer-upload',
  standalone: true,
  imports: [
    OuiStackComponent,
    OuiRowComponent,
    OuiTextComponent,
    OuiButtonComponent,
    OuiFileDropZoneComponent,
    CardComponent,
    PageComponent,
    SelectComponent,
    DownloadButtonComponent,
    LogTableComponent,
    ValidationErrorsDialogComponent,
    OuiBoxComponent,
    OuiCenterComponent,
  ],
  templateUrl: './buyer-upload.component.html',
})
export class BuyerUploadPage extends BasePageComponent {
  public buyerService = inject(BuyerManagementService);

  selectedFile = signal<File | undefined>(undefined);
  fileDropZone = viewChild<OuiFileDropZoneComponent>('fileDropZone');
  uploadForm = new FormGroup({
    entityCode: new FormControl(''),
  });
  dataSyncHistoryDatasource!: OuiDatasource<WorkflowLogEntry>;
  protected readonly validationErrors = signal<string[] | undefined>(undefined);
  private readonly notification = inject(NotificationService);
  protected readonly entities = inject(BusinessEntityService);

  constructor() {
    super();

    this.buyerService.buyerImportHistory.reload();
    effect(() => {
      const data = this.buyerService.buyerImportHistory.value();
      if (!data) return;
      this.dataSyncHistoryDatasource = new OuiDatasource(data);
    });
  }

  getDownloadFilename() {
    return `Buyer_${this.uploadForm.controls.entityCode.value || 'ALL'}_${
      new Date().toISOString().split('T')[0]
    }`;
  }

  handleFileAttached(file: File | undefined) {
    if (file) this.selectedFile.set(file);
  }

  uploadFile() {
    const file = this.selectedFile();
    const entity = this.uploadForm.controls.entityCode.value;
    if (!file || !entity) {
      this.notification.showAlert({
        message: 'Please select both Business Entity and File.',
        variant: 'warn',
        symbolName: 'error',
      });
      return;
    }

    this.buyerService.uploadBuyers(file, entity).subscribe({
      next: () => {
        this.fileDropZone()?.selectedFile.set(undefined);
        this.selectedFile.set(undefined);
        // this.showRedFlagBuyerDialog.set(false);
      },
      error: (err) => {
        const { errors } = err.error as { errors?: string[] };
        if (errors) {
          this.displayValidationErrors(errors);
        } else {
          const errorMessage =
            err.error || 'Failed to upload the file. Please try again.';
          this.notification.showAlert({
            message: errorMessage,
            variant: 'warn',
            symbolName: 'error',
          });
        }
      },
      complete: () => {
        this.notification.showSnackbar({
          message: 'Buyer Upload Successful',
          variant: 'success',
          symbolName: 'check',
        });
      },
    });
  }

  displayValidationErrors(errors: string[]) {
    this.validationErrors.set(errors);
  }
}
